import { loadStripe } from '@stripe/stripe-js';
import {
  environment,
  stripePublishableKey,
} from '../config/config';
import routesMap from '../Routes';
import { colors } from '../components/home/v4/styledComponents';
import { colors as v3FlavouredColors } from '../components/home/v3/styledComponents';
import { getBrowserInfo } from './mangopay';
import callApi from './api';
import { getCurrentLanguage } from './i18n/getCurrentLanguage';

import logoStripe from '../assets/logo-stripe.png';

export const publishableKey = stripePublishableKey;

let loadedStripe;

export const log = (message, level = 'info') => {
  if (['info', 'warn', 'error'].includes(level) === false) {
    level = 'warn';
  }

  /**
   * TODO: [POC] Payment System Provider | Migrating application performance monitoring dependency (Sentry)
   */
  if (typeof Raven !== 'undefined') {
    if (level === 'info' || level === 'warn') {
      Raven.captureMessage(message);
      return;
    }

    if (level === 'error') {
      if (message instanceof Error) {
        Raven.captureException(message);
        return;
      }

      Raven.captureException(JSON.stringify(message));
      return;
    }
  }

  if (['DEVELOPMENT', 'TEST'].includes(environment) === false) {
    return;
  }

  // eslint-disable-next-line no-console
  console[level](message);
};

export const areStripeRequirementsFulfilled = ({ paymentSystemContext }) => {
  if (paymentSystemContext?.stripe?.isOutOfCanaryReleaseScope) {
    return false;
  }

  if (typeof paymentSystemContext?.stripe?.intentId !== 'string') {
    return false;
  }

  return typeof paymentSystemContext?.stripe?.options?.clientSecret === 'string';
};

const startsWith = (subject, needle) => typeof subject === 'string' && subject.indexOf(needle) === 0;

export const useStripeConnectAsPaymentSystemProvider = () => startsWith(publishableKey, 'pk_test_') || startsWith(publishableKey, 'pk_live_');

export const getStripeClient = () => {
  if (!useStripeConnectAsPaymentSystemProvider()) {
    return {};
  }

  if (loadedStripe) {
    return loadedStripe;
  }

  loadedStripe = loadStripe(stripePublishableKey, { locale: getCurrentLanguage() });

  return loadedStripe;
};

export const appearanceApiOptions = () => ({
  // See [how to customize fontFamily variable?](https://stripe.com/docs/elements/appearance-api?platform=web#commonly-used-variables)
  // https://stripe.com/docs/js/elements_object/create#stripe_elements-options-fonts
  // https://stripe.com/docs/js/appendix/css_font_source_object
  fonts: [{ cssSrc: 'https://fonts.googleapis.com/css?family=Roboto:300,400,500,700' }],
  appearance: {
    rules: {
      '.Label': {
        fontSize: '16px',
        fontWeight: '700',
        textAlign: 'left',
        margin: '0 0 15px 0',
        color: `${colors.black}`,
        border: '1px solid #E0E6EB',
        boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(18, 42, 66, 0.02)',
      },
      '.Input': {
        fontFamily: 'Roboto',
        fontSize: '15px',
        fontWeight: '300',
        lineHeight: 'normal',
        margin: '0 0 15px 0',
        width: '100%',
        color: `${colors.black}`,
      },
      '.Input::placeholder': {
        fontFamily: 'Roboto',
        fontSize: '15px',
        fontWeight: '300',
        lineHeight: 'normal',
        margin: '0 0 15px 0',
        width: '100%',
        color: `${v3FlavouredColors.grey400}`,
      },
    },
  },
});

/**
 * @param {Object} setupIntent
 * @returns {{}|{publishableKey: ((function(): *)|*), baseUrl, isOutOfCanaryReleaseScope: (boolean|*),
 * options: {appearance: {rules: {".Input::placeholder": {margin: string,
 * fontFamily: string, color: string, width: string, fontSize: string, lineHeight: string, fontWeight: string},
 * ".Input": {margin: string, fontFamily: string, color: string, width: string,
 * fontSize: string, lineHeight: string, fontWeight: string},
 * ".Label": {border: string, boxShadow: string, margin: string, color: string, textAlign: string,
 * letterSpacing: string, fontSize: string, fontWeight: string}}}, fonts: [{cssSrc: string}],
 * clientSecret: (string|null|*), fields: {billingDetails: {address: {country: string}, name: string}}},
 * intentId, didFetchedStripeParams: boolean}}
 */
export const useStripe = ({ setupIntent }) => {
  if (!useStripeConnectAsPaymentSystemProvider()) {
    return {};
  }

  return {
    baseUrl: setupIntent.baseUrl,
    didFetchedStripeParams: true,
    intentId: setupIntent.intentId,
    options: {
      clientSecret: setupIntent.clientSecret,
      fields: {
        billingDetails: {
          name: 'auto',
          address: {
            country: 'never',
          },
        },
      },
      ...appearanceApiOptions(),
    },
    isOutOfCanaryReleaseScope: setupIntent.isOutOfCanaryReleaseScope,
    publishableKey: setupIntent.publishableKey,
  };
};

/**
 * Taking care of cases when
 * - postal code is not targeted
 * - no stripe-related keys is to be found
 * in [localStorage](https://developer.mozilla.org/fr/docs/Web/API/Window/localStorage)
 * - or some default values only has been stored in `localStorage`.
 * @param order
 * @param setStripe
 * @returns {Promise<{}|{baseUrl: string, postalCode: string, options: {}, intentId: string}>}
 */
export const setStripeState = ({ response, setStripe }) => {
  if (typeof response?.order?.addressSaved?.zipcode === 'undefined') {
    return {};
  }

  let stripe = {
    baseUrl: '',
    intentId: '',
    options: {},
    postalCode: '',
  };

  try {
    stripe = useStripe({ setupIntent: response.setupIntent });
    if (stripe && Object.keys(stripe).length >= 1) {
      setStripe({
        ...stripe,
        ...{ postalCode: response?.order?.addressSaved.zipcode },
      });
    }
  } catch (e) {
    log(e, 'error');
  }

  return stripe;
};

/**
 * - ❌ [Cancelling `SetupIntent`](https://stripe.com/docs/js/setup_intents/confirm_setup)
 *   for clients out of restricted zone (see canary release configuration parameters)
 * - ✅ [Confirming `SetupIntent`](https://stripe.com/docs/js/setup_intents/confirm_setup) after updating its metadata
 *   with order id and customer id
 */
export const confirmOrderIntent = ({
  baseUrl, elements, orderId, intentId,
}) => {
  const stripeClient = getStripeClient();

  // [How to confirm SetupIntents?](https://stripe.com/docs/js/setup_intents/confirm_card_setup)
  // [How to confirm PaymentIntents?](https://stripe.com/docs/js/payment_intents/confirm_payment)
  return elements
    .submit()
    .then((res) => {
      const { error: submitError } = res;
      if (submitError) {
        log(submitError, 'error');
      }

      /**
       * Both a `PaymentElement` instance (embedded in the `@stripe-js` `Elements` component) and
       * a `ClientSecret` (bound to a newly-created `SetupIntent`)
       * before [confirming such SetupIntent instance](https://stripe.com/docs/js/setup_intents/confirm_setup)
       * are passed to the matching [`confirmSetup`](https://stripe.com/docs/js/setup_intents/confirm_setup) method
       * of the [Stripe.js library](https://stripe.com/docs/stripe-js/react#setup)
       */
      const browserInfo = getBrowserInfo();
      return callApi(
        `public/intent/${orderId}`,
        'post',
        { browserInfo, intentId },
      );
    })
    .then(({ orderSetupIntent }) => stripeClient
      .then((stripe) => {
        stripe.confirmSetup({
          elements,
          clientSecret: orderSetupIntent?.setupIntent?.clientSecret,
          confirmParams: {
            return_url: `${baseUrl}${routesMap.SuccessPayment.url}#successful-setup-intent-confirmation`,
          },
        });
      })
      .catch((cannotConfirmSetup) => log(cannotConfirmSetup, 'error')));
};

export const registerPaymentMethod = ({
  baseUrl, elements, customerId,
}) => {
  const stripeClient = getStripeClient();

  return elements
    .submit()
    .then((res) => {
      const { error: submitError } = res;
      if (submitError) {
        log(submitError, 'error');
      }

      return callApi(
        `public/intent/customer/${customerId}`,
        'post',
      );
    })
    .then((wrappedIntent) => stripeClient
      .then((stripe) => {
        stripe.confirmSetup({
          elements,
          clientSecret: wrappedIntent?.clientSecret,
          confirmParams: {
            return_url: `${baseUrl}${routesMap.SuccessPayment.url}#successful-setup-intent-confirmation`,
          },
        });
      })
      .catch((cannotRegisterPaymentMethod) => log(cannotRegisterPaymentMethod, 'error')));
};

export const stripeLogo = logoStripe;
